import { PatientForm } from "@/dentlab/src/forms/form-components/patient/insert-update/patient.form";
import { PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { SheetContentProps } from "../../sheet-provider/sheetRegistry";
import { PatientJobs } from "./components/PatientJobs";
import { useSheetStore } from "../../sheet.store";
import { PatientCoreForm } from "@/dentlab/src/forms/form-components/patient/insert-update/patient-core.form";
import { PatientExtendedForm } from "@/dentlab/src/forms/form-components/patient/insert-update/patient-extended.form";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTriggerStandard,
} from "@/dentlab/src/components/ui/collapsible";
import { useState } from "react";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { isPatientEntityType } from "@/dentlab/src/store/Patient/patients.store";

interface PatientSheetContentProps extends SheetContentProps {
	patient?: PatientEntityType;
	onSave?: (patient: PatientEntityType) => void;
}

export const PatientSheetContent: React.FC<PatientSheetContentProps> = ({
	sheetKey,
	patient: initialPatient,
	onSave,
}) => {
	const updatePendingChange = useSheetStore(
		(state) => state.updatePendingChange
	);
	const { patientFromStore, patientName } = usePatientStore((state) => ({
		patientFromStore: initialPatient?.id
			? state.patientsLookup[initialPatient.id]
			: null,
		patientName: state.patientName,
	}));
	const isTypePatientEntityType =
		patientFromStore && isPatientEntityType(patientFromStore);
	const patient = isTypePatientEntityType ? patientFromStore : initialPatient;

	const handleChanges = (isPendingChanges: boolean) => {
		updatePendingChange(sheetKey, isPendingChanges);
	};

	const [corePatientOpen, setCorePatientOpen] = useState(false);
	const [extendedPatientOpen, setExtendedPatientOpen] = useState(false);
	const [jobHistoryOpen, setJobHistoryOpen] = useState(true);

	return (
		<div className="flex flex-col h-full">
			<div className="flex flex-col">
				<div className="flex flex-row items-end gap-2 px-4 py-4">
					<div className="text-lg font-bold">
						{patient ? patientName(patient) : ""}
					</div>
					<div className="text-md text-gray-700">
						{patient?.code ?? ""}
					</div>
					<div className="text-md text-gray-700">
						{patient?.birth_date
							? new Date(patient.birth_date).toLocaleDateString(
									"de-DE"
								)
							: ""}
					</div>
				</div>
				{patient && (
					<PatientForm
						patientId={patient?.id as string}
						onSave={onSave}
						setIsPendingChanges={handleChanges}
						withinSheet
					>
						<Collapsible
							open={corePatientOpen}
							onOpenChange={setCorePatientOpen}
							className="border-b-0"
						>
							<CollapsibleTriggerStandard
								open={corePatientOpen}
								className="text-md"
							>
								<div>Patientendaten</div>
							</CollapsibleTriggerStandard>
							<CollapsibleContent>
								<PatientCoreForm autoFocusCode={false} />
							</CollapsibleContent>
						</Collapsible>
						<Collapsible
							open={extendedPatientOpen}
							onOpenChange={setExtendedPatientOpen}
						>
							<CollapsibleTriggerStandard
								open={extendedPatientOpen}
								className="text-md"
							>
								<div>Weitere Patientendaten</div>
							</CollapsibleTriggerStandard>
							<CollapsibleContent>
								<PatientExtendedForm />
							</CollapsibleContent>
						</Collapsible>
					</PatientForm>
				)}
			</div>
			{patient && (
				<Collapsible
					open={jobHistoryOpen}
					onOpenChange={setJobHistoryOpen}
					className="border-t-0"
				>
					<CollapsibleTriggerStandard
						open={jobHistoryOpen}
						className="text-md"
					>
						<div>Historie</div>
					</CollapsibleTriggerStandard>
					<CollapsibleContent>
						<PatientJobs patientId={patient.id as string} />
					</CollapsibleContent>
				</Collapsible>
			)}
		</div>
	);
};
